/**
 * ============================================================
 * EDEL CRM — AntD ConfigProvider tema oluşturucu
 * ============================================================
 *
 * Edel paletini (tokens.ts) AntD v5 ThemeConfig'e çevirir:
 * global token'lar + bileşen düzeyi override'lar + algoritma.
 * Tüm renkler palet'ten gelir — hardcode YOK.
 */

import { theme as antdTheme, type ThemeConfig } from "antd";
import {
  getPalette,
  fontFamily,
  radius,
  type ThemeMode,
} from "./tokens";

export const buildAntdTheme = (mode: ThemeMode): ThemeConfig => {
  const p = getPalette(mode);
  const isDark = mode === "dark";

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      // Marka & durum
      colorPrimary: p.primary,
      colorSuccess: p.success,
      colorWarning: p.warning,
      colorError: p.error,
      colorInfo: p.info,
      colorLink: p.primary,

      // Yüzey hiyerarşisi (elevation)
      colorBgBase: p.bg,
      colorBgLayout: p.bg,
      colorBgContainer: p.surface,
      colorBgElevated: p.elevated,
      // Tooltip zemini — KOYU tutulur (yazı beyaz kalır) → her iki modda okunur.
      // (Önceden surface2 = açık kremdi, beyaz yazı okunmuyordu.)
      colorBgSpotlight: isDark ? "#334155" : "#1e293b",

      // Metin
      colorText: p.text,
      colorTextSecondary: p.textMuted,
      colorTextTertiary: p.textSoft,

      // Kenarlık
      colorBorder: p.border,
      colorBorderSecondary: p.borderLight,

      // Şekil & tipografi
      borderRadius: radius.md,
      borderRadiusLG: radius.lg,
      borderRadiusSM: radius.sm,
      fontFamily,
      fontSize: 14,
      controlHeight: 36,
      wireframe: false,

      // Gölgeler — premium yumuşak derinlik
      boxShadow: p.shadowMd,
      boxShadowSecondary: p.shadowLg,
      boxShadowTertiary: p.shadowSm,
    },
    components: {
      Layout: {
        headerBg: p.surface,
        bodyBg: p.bg,
        siderBg: p.surface,
        headerHeight: 64,
        headerPadding: "0 20px",
        lightSiderBg: p.surface,
        triggerBg: p.surface2,
        triggerColor: p.textMuted,
      },
      Menu: {
        // Şeffaf zemin → marka turuncu seçili/hover state
        itemBg: "transparent",
        subMenuItemBg: "transparent",
        itemColor: p.textMuted,
        itemHoverColor: p.primary,
        itemHoverBg: isDark ? "rgba(249,115,22,0.10)" : p.primarySoft,
        itemSelectedColor: isDark ? p.primaryLight : p.primaryDark,
        itemSelectedBg: isDark ? "rgba(249,115,22,0.16)" : p.primarySoft,
        itemActiveBg: isDark ? "rgba(249,115,22,0.16)" : p.primarySoft,
        itemBorderRadius: radius.sm,
        itemMarginInline: 8,
        itemMarginBlock: 4,
        itemHeight: 42,
        iconSize: 17,
        collapsedIconSize: 18,
        groupTitleColor: p.textSoft,
      },
      Card: {
        colorBgContainer: p.surface,
        borderRadiusLG: radius.card,
        boxShadowTertiary: p.shadowSm,
        headerFontSize: 16,
        headerHeight: 52,
        paddingLG: 20,
      },
      Table: {
        headerBg: p.surface2,
        headerColor: p.textMuted,
        headerSplitColor: "transparent",
        rowHoverBg: isDark ? "rgba(249,115,22,0.07)" : p.surface3,
        borderColor: p.borderLight,
        headerBorderRadius: radius.sm,
        cellPaddingBlock: 12,
      },
      Button: {
        borderRadius: radius.button,
        borderRadiusLG: radius.md,
        borderRadiusSM: radius.sm,
        controlHeight: 36,
        controlHeightLG: 44,
        fontWeight: 500,
        primaryShadow: "none",
        defaultShadow: "none",
        dangerShadow: "none",
      },
      Modal: {
        contentBg: p.elevated,
        headerBg: p.elevated,
        borderRadiusLG: radius.lg,
        titleFontSize: 17,
      },
      Drawer: {
        colorBgElevated: p.surface,
      },
      Input: {
        borderRadius: radius.input,
        controlHeight: 36,
        activeShadow: `0 0 0 3px ${p.ring}`,
        activeBorderColor: p.primary,
        hoverBorderColor: p.primaryLight,
        colorBgContainer: p.surface,
      },
      InputNumber: {
        borderRadius: radius.input,
        controlHeight: 36,
        activeShadow: `0 0 0 3px ${p.ring}`,
        activeBorderColor: p.primary,
      },
      Select: {
        borderRadius: radius.input,
        controlHeight: 36,
        optionSelectedBg: isDark ? "rgba(249,115,22,0.16)" : p.primarySoft,
        optionSelectedColor: isDark ? p.primaryLight : p.primaryDark,
        activeBorderColor: p.primary,
        hoverBorderColor: p.primaryLight,
        activeOutlineColor: p.ring,
      },
      DatePicker: {
        borderRadius: radius.input,
        controlHeight: 36,
        activeBorderColor: p.primary,
        activeShadow: `0 0 0 3px ${p.ring}`,
      },
      Tag: {
        borderRadiusSM: radius.sm,
        defaultBg: p.surface2,
        defaultColor: p.textMuted,
      },
      Segmented: {
        borderRadius: radius.sm,
        borderRadiusSM: radius.sm,
        trackBg: p.surface2,
        itemSelectedBg: p.surface,
        itemSelectedColor: p.primary,
        itemColor: p.textMuted,
        itemHoverColor: p.primary,
      },
      Tabs: {
        inkBarColor: p.primary,
        itemSelectedColor: p.primary,
        itemHoverColor: p.primaryLight,
        itemActiveColor: p.primary,
        titleFontSize: 14,
        horizontalItemGutter: 24,
      },
      Statistic: {
        contentFontSize: 28,
        titleFontSize: 13,
      },
      Progress: {
        defaultColor: p.primary,
      },
      Switch: {
        handleBg: "#ffffff",
      },
      Avatar: {
        colorTextPlaceholder: p.primary,
      },
      Badge: {
        colorBgContainer: p.surface,
      },
      Popover: {
        colorBgElevated: p.elevated,
        borderRadiusLG: radius.md,
      },
      Dropdown: {
        colorBgElevated: p.elevated,
        borderRadiusLG: radius.md,
      },
      Tooltip: {
        borderRadius: radius.sm,
      },
      Notification: {
        borderRadiusLG: radius.md,
      },
      Message: {
        contentBg: p.elevated,
      },
      Divider: {
        colorSplit: p.borderLight,
      },
      Descriptions: {
        labelBg: p.surface2,
        colorSplit: p.borderLight,
      },
      Alert: {
        borderRadiusLG: radius.md,
      },
    },
  };
};
